import { ChevronDownIcon } from '@chakra-ui/icons'
import type {
  GridProps} from '@chakra-ui/react'
import {
  Flex,
  forwardRef,
  Grid,
  Spinner,
  Text,
} from '@chakra-ui/react'
import type { AppInfoMessage } from 'BrokerTsLib/src/dtos'
import React from 'react'

import { NewsItem, NewsItemGrid, TimelineItemGrid } from './NewsItem'

export interface NewsProps extends GridProps {
  news?: AppInfoMessage[]
  isLoading?: boolean
  onSetRead: (id: number) => void
  onMoreClick?: () => void
}

export const News = forwardRef(
  (
    {
      children,
      sx,
      news,
      isLoading,
      onSetRead,
      onMoreClick,
      ...rest
    }: NewsProps,
    ref,
  ) => {
    return (
      <Grid
        ref={ref}
        sx={{
          mb: {
            base: 4,
            md: 4,
          },
          ...sx,
        }}
        {...rest}
      >
        {news?.map((newItem, index) => (
          <React.Fragment key={index}>
            <NewsItem
              {...newItem}
              onSetRead={onSetRead}
              first={index === 0}
              last={!onMoreClick && index === news.length - 1}
            />

            {index < news.length - 1 &&
              hasYearDifference(newItem, news[index + 1]) && (
              <YearDivider
                year={news[index + 1].messageDate?.getFullYear() ?? 0}
              />
            )}
          </React.Fragment>
        ))}

        {(news?.length ?? 0) > 0 && onMoreClick && (
          <LoadMore onClick={() => onMoreClick()} />
        )}

        {isLoading && <LoadingMore />}

        {children}
      </Grid>
    )
  },
)

const hasYearDifference = (a: AppInfoMessage, b: AppInfoMessage) => {
  return (
    (a.messageDate ?? new Date()).getFullYear() !==
    (b.messageDate ?? new Date()).getFullYear()
  )
}

const YearDivider = ({ year }: { year: number }) => (
  <NewsItemGrid mt={-4} mb={-4}>
    <TimelineItemGrid
      color="neutral.700"
      bg="transparent"
      fontSize="md"
      fontWeight="light"
    >
      {year}
    </TimelineItemGrid>
  </NewsItemGrid>
)

const LoadMore = ({ onClick }: { onClick: () => void }) => (
  <NewsItemGrid mt={-4} mb={-4} role="group">
    <TimelineItemGrid
      color="neutral.300"
      bg="transparent"
      borderColor="neutral.100"
      border="1px solid"
      fontSize="md"
      fontWeight="light"
      last={true}
      onClick={onClick}
      cursor="pointer"
    >
      <ChevronDownIcon boxSize={5} />
    </TimelineItemGrid>
    <Flex>
      <Text
        my={2.5}
        color="neutral.500"
        cursor="pointer"
        onClick={onClick}
        _groupHover={{ textDecor: 'underline' }}
      >
        Více aktualit...
      </Text>
    </Flex>
  </NewsItemGrid>
)

const LoadingMore = () => (
  <NewsItemGrid mt={-4} mb={-4} role="group">
    <TimelineItemGrid
      color="neutral.300"
      bg="transparent"
      fontSize="md"
      fontWeight="light"
      last={true}
    >
      <Spinner h={9} w={9} thickness="3px" speed=".7s" />
    </TimelineItemGrid>
    <Flex>
      <Text my={2.5}>Načítání...</Text>
    </Flex>
  </NewsItemGrid>
)
